import React, { useEffect, useState, useCallback } from 'react';
import './AdminPartners.css';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import PartnerStatsAdmin from '../../components/PartnerStatsAdmin/PartnerStatsAdmin';
import PartnersTable from '../../components/PartnersTable/PartnersTable';
import ConvertUserModal from '../../components/ConvertUserModal/ConvertUserModal';
import Alert from '../../components/Alert/Alert';

const AdminPartners = () => {
    const [stats, setStats] = useState(null);
    const [partners, setPartners] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalVisible, setModalVisible] = useState(false);
    const [alert, setAlert] = useState({ show: false, message: '', type: '' });
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 20,
        total: 0,
        last_page: 1
    });

    // Función para mostrar alertas
    const showAlert = (message, type = 'info') => {
        setAlert({ show: true, message, type });
        setTimeout(() => setAlert({ show: false, message: '', type: '' }), 5000);
    };

    // Obtener estadísticas generales
    const fetchStats = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch('https://api.minymol.com/admin/earnings/stats', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!res.ok) throw new Error('Error al obtener estadísticas');

            const data = await res.json();
            setStats(data);
        } catch (error) {
            console.error('Error fetching stats:', error);
            showAlert('Error al cargar estadísticas', 'error');
        }
    }, []);

    // Obtener lista de partners
    const fetchPartners = useCallback(async (page = 1) => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`https://api.minymol.com/admin/earnings/partners?page=${page}&limit=20`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!res.ok) throw new Error('Error al obtener partners');

            const data = await res.json();
            setPartners(data.data);
            setPagination(data.pagination);
        } catch (error) {
            console.error('Error fetching partners:', error);
            showAlert('Error al cargar partners', 'error');
        }
    }, []);

  // Obtener usuarios disponibles para convertir
  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('https://api.minymol.com/users', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Error al obtener usuarios');
      
      const data = await res.json();
      // Filtrar solo usuarios que NO son partners
      const availableUsers = data.filter(user => !user.isPartner);
      setUsers(availableUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      showAlert('Error al cargar usuarios', 'error');
    }
  }, []);    // Convertir usuario a partner
    const convertToPartner = async (userId) => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`https://api.minymol.com/admin/earnings/partners/${userId}/convert`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!res.ok) throw new Error('Error al convertir usuario');

            const data = await res.json();

            if (data.success) {
                showAlert(data.message, 'success');
                fetchPartners(); // Recargar lista de partners
                fetchUsers(); // Recargar lista de usuarios disponibles
                fetchStats(); // Actualizar estadísticas
            } else {
                showAlert(data.message, 'error');
            }
        } catch (error) {
            console.error('Error converting user:', error);
            showAlert('Error al convertir usuario a partner', 'error');
        }
    };

    // Convertir múltiples usuarios
    const convertMultipleUsers = async (userIds) => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch('https://api.minymol.com/admin/earnings/partners/convert-multiple', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ userIds })
            });

            if (!res.ok) throw new Error('Error al convertir usuarios');

            const data = await res.json();

            if (data.success) {
                const { exitosos, fallidos, total } = data.results;
                showAlert(`Conversión completada: ${exitosos}/${total} exitosos, ${fallidos} fallidos`, 'success');
                fetchPartners();
                fetchUsers();
                fetchStats();
            } else {
                showAlert('Error en la conversión múltiple', 'error');
            }
        } catch (error) {
            console.error('Error converting multiple users:', error);
            showAlert('Error al convertir usuarios', 'error');
        }
    };

    // Revocar status de partner
    const revokePartner = async (userId) => {
        if (!window.confirm('¿Estás seguro de revocar el status de partner?')) return;

        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`https://api.minymol.com/admin/earnings/partners/${userId}/revoke`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!res.ok) throw new Error('Error al revocar partner');

            const data = await res.json();

            if (data.success) {
                showAlert(data.message, 'success');
                fetchPartners();
                fetchUsers();
                fetchStats();
            } else {
                showAlert(data.message, 'error');
            }
        } catch (error) {
            console.error('Error revoking partner:', error);
            showAlert('Error al revocar partner', 'error');
        }
    };

    // Cargar datos iniciales
    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await Promise.all([
                fetchStats(),
                fetchPartners(),
                fetchUsers()
            ]);
            setLoading(false);
        };

        loadData();
    }, [fetchStats, fetchPartners, fetchUsers]);

    if (loading) {
        return (
            <div className="admin-partners">
                <Header />
                <div className="admin-partners__container">
                    <div className="loading">Cargando panel de administración...</div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="admin-partners">
            <Header />

            <div className="admin-partners__container">
                <div className="admin-partners__header">
                    <h1>🎯 Administración de Partners</h1>
                    <button
                        className="btn-primary"
                        onClick={() => setModalVisible(true)}
                    >
                        ➕ Convertir Usuarios
                    </button>
                </div>

                {/* Alertas */}
                {alert.show && (
                    <Alert
                        message={alert.message}
                        type={alert.type}
                        onClose={() => setAlert({ show: false, message: '', type: '' })}
                    />
                )}

                {/* Estadísticas */}
                <PartnerStatsAdmin stats={stats} />

                {/* Tabla de Partners */}
                <PartnersTable
                    partners={partners}
                    pagination={pagination}
                    onPageChange={fetchPartners}
                    onRevokePartner={revokePartner}
                />

                {/* Modal para convertir usuarios */}
                {modalVisible && (
                    <ConvertUserModal
                        users={users}
                        onClose={() => setModalVisible(false)}
                        onConvertSingle={convertToPartner}
                        onConvertMultiple={convertMultipleUsers}
                    />
                )}
            </div>

            <Footer />
        </div>
    );
};

export default AdminPartners;
